// ── the community space — what it holds ─────────────────────────────────────
// /community is the public hub: skills · guides · resources. Everything on it
// is one of three species below, and every species says WHO keeps it, because
// the whole point of a public place is that you can tell whose position you're
// reading. Client-safe: no node built-ins here (the loaders live next door).

/** who a thing comes from — the eyebrow on every card and every page. */
export type Origin =
  /** kept by innernet itself (content/skills, content/guides) */
  | { kind: "innernet" }
  /** kept by a member — a skill they published from their own memory */
  | { kind: "member"; name: string; ownerKey?: string | null; avatar?: string | null }
  /** kept by someone elsewhere on the open internet — ingested with attribution
   *  (our link, their name, the source named), or pointed at when its licence
   *  forbids copying */
  | { kind: "web"; name: string; url: string; host?: string };

/** the reading label for an origin — "innernet" · "jonna" · "anthropic". */
export function originName(o: Origin): string {
  return o.kind === "innernet" ? "innernet" : o.name;
}

// ── skills ──────────────────────────────────────────────────────────────────

/** the categories a skill can file under — the filter chips. Deliberately
 *  short: a lens is only useful while every option still means something. */
export const SKILL_CATEGORIES = [
  "design",
  "code",
  "writing",
  "workflow",
  "documents",
  "memory",
  "art",
] as const;
export type SkillCategory = (typeof SKILL_CATEGORIES)[number];

/** tag → category, so a skill's own tags decide where it files unless its
 *  manifest names a `category` outright. */
const TAG_TO_CATEGORY: Record<string, SkillCategory> = {
  design: "design", ui: "design", ux: "design", frontend: "design", css: "design", "design-system": "design",
  accessibility: "design", motion: "design", brand: "design", typography: "design",
  code: "code", coding: "code", react: "code", nextjs: "code", typescript: "code", python: "code",
  testing: "code", debugging: "code", tdd: "code", mcp: "code", api: "code", engineering: "code",
  writing: "writing", docs: "writing", documentation: "writing", comms: "writing", copy: "writing",
  workflow: "workflow", process: "workflow", planning: "workflow", agents: "workflow", skills: "workflow",
  format: "workflow", spec: "workflow", ai: "workflow",
  documents: "documents", pdf: "documents", docx: "documents", xlsx: "documents", pptx: "documents",
  spreadsheets: "documents", slides: "documents",
  memory: "memory", context: "memory", innernet: "memory",
  art: "art", generative: "art", canvas: "art", visual: "art",
};

export function categoryFor(tags: string[], explicit?: string | null): SkillCategory | "other" {
  const e = (explicit ?? "").toLowerCase().trim();
  if ((SKILL_CATEGORIES as readonly string[]).includes(e)) return e as SkillCategory;
  for (const t of tags) {
    const c = TAG_TO_CATEGORY[t.toLowerCase().trim()];
    if (c) return c;
  }
  return "other";
}

export interface CommunitySkill {
  /** stable id inside the listing — `innernet:<name>` · `share:<token>` · `web:<slug>` · `commons:<slug>` */
  key: string;
  /** where opening the card goes: /skills/<name> · /share/<token> · the source url */
  href: string;
  /** the link an AI can be handed — /skills/<name>/raw · the share url · the raw source */
  pasteUrl: string;
  title: string;
  description: string;
  when_to_load?: string;
  tags: string[];
  category: SkillCategory | "other";
  origin: Origin;
  /** the author, when innernet keeps a skill someone else wrote ("kept by innernet · by <name>") */
  by?: string;
  /** the member who BROUGHT it here, when the skill is somebody else's — the
   *  second attribution. The origin says whose it is; this says who carried it
   *  in. Absent when the two are the same person. */
  broughtBy?: { name: string; avatar?: string | null };
  version?: string;
  /** ISO date of the last substantive change — "always the latest" made visible */
  updated?: string;
  minutes?: number;
  /** how much it has been used (migration 109): copies · saves · pulls · opens
   *  folded into one score for the `top` view, and the raw total for the card. */
  uses?: { score: number; total: number; copies: number; saves: number; pulls: number; opens: number };
  /** where the skill itself lives (its folder on github, a gist, a post) — the
   *  repo in it is what gathers skills into a package (./packs.ts) */
  sourceUrl?: string;
  /** true when the href leaves innernet.live (a card says so; nothing pretends) —
   *  only the pointed web skills now; an ingested one opens on our own page */
  external: boolean;
}

// ── guides ──────────────────────────────────────────────────────────────────

export interface GuideMeta {
  slug: string;
  title: string;
  /** what you'll be able to do after reading — the card line */
  description: string;
  /** a coarse lens: start · connect · capture · share · skills */
  category: string;
  updated?: string;
  minutes: number;
  origin: Origin;
  /** the keeper's reading order on the index (lower first; 99 when unsaid) */
  order: number;
  /** the chapter this guide is a step of (lib/community/chapters.ts), and its
   *  place in it, so the reader can say "step 3 of 5" and hand you the next */
  chapter?: string;
  step?: number;
  /** the guide's colour: its thumbnail, its screenshots and its room */
  hue: number;
  /** the first real screenshot in it — the picture on its thumbnail */
  hero?: string;
  /** kept for old links: a guide from before chapters */
  series?: string;
}

export interface Guide extends GuideMeta {
  body: string;
  outline: { id: string; text: string }[];
}

// ── resources ───────────────────────────────────────────────────────────────

/** the kinds a resource can be — the filter chips on the resources view. */
export const RESOURCE_KINDS = ["ai tools", "editors", "mcp", "skills", "reading"] as const;
export type ResourceKind = (typeof RESOURCE_KINDS)[number];

export interface Resource {
  key: string;
  name: string;
  /** one quiet line: what it is and why it's here */
  description: string;
  url: string;
  kind: ResourceKind;
  /** the BrandIcon key when a real mark exists (never emoji) */
  brand?: string;
  /** true when innernet connects to it directly */
  connects?: boolean;
}

// ── the spaces of the hub ───────────────────────────────────────────────────

export const COMMUNITY_SPACES = [
  { key: "skills", label: "skills" },
  { key: "guides", label: "guides" },
  { key: "resources", label: "resources" },
  { key: "about", label: "about" },
] as const;
export type CommunitySpace = (typeof COMMUNITY_SPACES)[number]["key"];

export const COMMUNITY_DEKS: Record<CommunitySpace, string> = {
  skills: "things an ai can learn from a link, kept by innernet, by members, and by people around the web.",
  guides: "short, honest walkthroughs for the parts of innernet worth learning once.",
  resources: "the tools, clients and reading that go well with a memory of your own.",
  about: "who is here, what they keep, and how a skill finds its way from one person to every ai you use.",
};

export function communityHref(space: CommunitySpace): string {
  return space === "skills" ? "/community" : `/community/${space}`;
}
